import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CcmReturnPlanStatus, PendingCcmTime } from 'src/app/models/ccm/ccm-pending-time.model';
import { AreYouSureModalComponent } from 'src/app/shared/are-you-sure-modal/are-you-sure-modal.component';
import { PatientTimerService } from '../patient-timer.service';
import { PatientService } from '../patient.service';
import { SnackService } from '../snack.service';

/**
 * CCM care plan time, S2.4 (BRD-CLIN-2026-008 FR-4, mockup v0.2 screens 4–6): the time entry a nurse
 * left to build a care plan because the patient had none, and the way back to it. One entry at a time,
 * for one patient, held in memory only: a reload or another patient drops it, and the nurse is told it
 * was not saved. The entry comes back as the nurse left it. The chart timer is cleared on the way out, as
 * on any close, so nothing else can save the same minutes again, and left alone on the way back: what it
 * counts by then is new work, saved nowhere else.
 *
 * Also the channel between the bar on top of the patient chart and the Care Plans tab below it: the
 * tab says whether the care plan can be saved yet, and the bar asks the tab to save it.
 */
@Injectable({
  providedIn: 'root',
})
export class CcmCarePlanReturnService {
  private readonly pendingSubject = new BehaviorSubject<PendingCcmTime | null>(null);
  private readonly planStatusSubject = new BehaviorSubject<CcmReturnPlanStatus>({ ready: false, saving: false });
  private readonly saveRequestsSubject = new Subject<void>();

  constructor(
    private router: Router,
    private timer: PatientTimerService,
    private patientService: PatientService,
    private snackBar: SnackService,
    private dialog: MatDialog
  ) {}

  get pending$(): Observable<PendingCcmTime | null> {
    return this.pendingSubject.asObservable();
  }

  get pending(): PendingCcmTime | null {
    return this.pendingSubject.value;
  }

  /** Whether the care plan the waiting entry needs can be saved yet, as the Care Plans tab sees it. */
  get planStatus$(): Observable<CcmReturnPlanStatus> {
    return this.planStatusSubject.asObservable();
  }

  /** The bar's "Save and return to time", for the Care Plans tab to act on. */
  get saveRequests$(): Observable<void> {
    return this.saveRequestsSubject.asObservable();
  }

  /** The entry waiting on `patientId`, if any. */
  pendingFor(patientId: string): PendingCcmTime | null {
    const pending = this.pending;
    return pending && pending.patientId === patientId ? pending : null;
  }

  /**
   * Holds the entry and takes the nurse to the patient's Care Plans tab (FR-4.1, FR-4.2). Only one entry
   * waits at a time: one already waiting is dropped, and the nurse told so, rather than silently lost.
   */
  leave(pending: PendingCcmTime): void {
    this.discard();
    this.planStatusSubject.next({ ready: false, saving: false });
    this.pendingSubject.next(pending);
    this.router.navigate(['clinical', 'patient-detail', pending.patientId, 'care-plans']);
  }

  setPlanStatus(status: CcmReturnPlanStatus): void {
    this.planStatusSubject.next(status);
  }

  requestSave(): void {
    this.saveRequestsSubject.next();
  }

  /**
   * Reopens the time form as the nurse left it (FR-4.3); the picker loads the care plans again, so the
   * new one is there. Never for another patient: the form saves against the patient open now.
   */
  returnToTime(planSaved: boolean): void {
    const pending = this.pending;
    if (!pending) {
      return;
    }
    if (this.patientService.currentPatientService?.user_id !== pending.patientId) {
      this.discard();
      return;
    }
    this.pendingSubject.next(null);
    // The timer was cleared when the entry was left: whatever it counts now, the nurse started while building
    // the plan, so closing the restored entry must not clear it.
    this.timer.openTimeDialog({ ...pending.dialogData, restore: pending.restore });
    this.snackBar.genericSnackBar(
      `${planSaved ? 'Care plan saved. ' : ''}Your time entry is as you left it.`,
      ['success-snackbar'],
      4000
    );
  }

  /** Drops the entry and says so (FR-4.4). */
  discard(): void {
    if (!this.pending) {
      return;
    }
    this.pendingSubject.next(null);
    this.snackBar.genericSnackBar('The CCM time entry was not saved.', ['warn-snackbar'], 6000);
  }

  /**
   * Asks before the entry is dropped on leaving the Care Plans tab or the patient. Answers whether the
   * nurse chose to discard it; dropping it is left to the caller, once the whole leave is decided.
   */
  confirmDiscard$(): Observable<boolean> {
    return this.dialog
      .open(AreYouSureModalComponent, {
        width: '500px',
        data: {
          title: 'Your CCM time is not saved',
          body: '<p>You left the time form to build a care plan. If you leave now, that time entry is discarded.</p>',
          cancelButton: 'Stay',
          confirmButton: 'Discard time',
        },
      })
      .afterClosed()
      .pipe(map((result) => result?.confirmed === true));
  }
}
